/**
 * Section components for **Detection** entity-kind in the Entity Details
 * panel (REQ-UI-066). Renders the SAPIENT Detection section schema from
 * ADR-032 §4, populated from ``GET /api/query/detection/{id}/details`` via
 * :func:`useDetectionDetail` (PR5a inc 4; supersedes the inc 1 placeholder).
 *
 * Sections (mirrors the Event/Aggregator ``SectionShell`` + ``SectionRow``
 * pattern; a section shows an empty note when its optional nested data is
 * absent rather than disappearing — operator-visibility, CLAUDE.md §8.1):
 *
 *   1. IDENTITY — report_id, object_id, state, detection time.
 *   2. CLASSIFICATION — the full ``classification[]`` tree (primary first),
 *      each type + confidence + nested ``sub_class`` refinements.
 *   3. TRACK / OBJECT INFO — the ``track_info[]`` / ``object_info[]``
 *      key/value(/error) lists.
 *   4. GEOMETRY — the projected WGS84 location (+ velocity + colour). Pushing
 *      to the map is the Detections-panel row right-click → "Select on Map"
 *      (REQ-UI-096); no per-section button (removed 2026-06-09 as redundant).
 *   5. SIGNAL — the ``signal[]`` RF characteristics.
 *   6. BEHAVIOUR — the ``behaviour[]`` list (type + confidence).
 *   7. ASSOCIATIONS — ``associated_detection[]`` (cross-pane-linked) +
 *      ``associated_file[]`` (type + url; not auto-fetched).
 *   8. DETECTED BY — a cross-pane link to the ``producer_id`` platform.
 *   9. PROVENANCE — source aggregator, CDS source-network, ingress time,
 *      schema version, message hash.
 *
 * The selection bus carries only the ``report_id``; the hook fetches the
 * rest. A ``404`` resolves to ``null`` (the detection aged out of the
 * queryable window) — an empty state, not an error.
 *
 * Section **visibility** and **expand/collapse** are read from
 * :func:`useSectionVisibility("detection")` per REQ-UI-092 / REQ-UI-064:
 * a section the operator hides in the gear menu is elided entirely
 * (ADR-024 §4), and each rendered shell self-wires its collapsed default
 * off the :func:`EntityDetailsSectionsProvider`. (This realizes the
 * collapsible-section behavior previously flagged for api-change-request
 * #358 — now **superseded**: the persisted ``expanded`` state lives in
 * the v3 ``entity_details`` model per ADR-033 §10, so no backend change
 * is needed.)
 *
 * Cross-references:
 *   - REQ-UI-066 (Detection section schema) / REQ-UI-051 (DETECTED BY
 *     cross-pane select) / REQ-UI-014 (CDS provenance).
 *   - REQ-UI-092 (per-type visibility + expand/collapse defaults) /
 *     REQ-UI-064 (collapsible sections, all entity types).
 *   - REQ-QUERY-024 (Detection detail surface).
 *   - ADR-032 §4 (Detection data model) / ADR-024 §4 (panel structure) /
 *     ADR-033 §10 (relocated section config).
 */
import { SectionShell, SectionRow, SectionPlaceholder } from "@/entity-details/section-shell";
import { useDetectionDetail, type DetectionDetail } from "@/entity-details/use-detection-detail";
import { useObjectTrackDetail } from "@/entity-details/use-object-track-detail";
import { isPresentedTrack, useObjectTrackById } from "@/object-tracks/object-track-store";
import { formatMembership, formatMembershipFromRecord } from "@/object-tracks/track-display";
import { useSectionVisibility } from "@/entity-details/use-section-visibility";
import { EntityDetailsSectionsProvider } from "@/entity-details/entity-details-sections-context";
import { recordTypeForEntityKind } from "@/entity-details/entity-record-mapping";
import { useSelectionActions } from "@/selection/selection-store";
import { useInspectInList } from "@/selection/use-inspect-in-list";
import { classificationLabel, formatConfidence } from "@/detections/detection-display";
import { usePreferences } from "@/preferences/preferences-context";
import { useProducerLatestStatus } from "@/entity-details/use-producer-latest-status";
import { useProducerNames } from "@/lib/use-producer-names";
import { shortId } from "@/lib/short-id";
import { SourceBytesSection } from "@/query/source-bytes-section";
import { formatCoordinate } from "@/units/format-coordinate";
import type { components } from "@/lib/api-types.gen";

type DetectionClassification = components["schemas"]["DetectionClassification"];
type DetectionSubClass = components["schemas"]["DetectionSubClass"];
type DetectionKeyValue = components["schemas"]["DetectionKeyValue"];
type DetectionSignal = components["schemas"]["DetectionSignal"];
type DetectionBehaviour = components["schemas"]["DetectionBehaviour"];
type DetectionAssociatedDetection = components["schemas"]["DetectionAssociatedDetection"];
type DetectionAssociatedFile = components["schemas"]["DetectionAssociatedFile"];

export function DetectionSections({ reportId }: { reportId: string }): React.ReactElement {
  const query = useDetectionDetail({ reportId });
  // Two-slot routing (2026-06-09): cross-pane links (DETECTED BY platform,
  // associated detections) *navigate* Entity Details, so they ``inspect``
  // (inspected slot only — leave the map ring put). The map push is the
  // Detections-panel row right-click → "Select on Map" (the single
  // push-to-map affordance; REQ-UI-096). The DETECTED BY **platform** link
  // additionally surfaces the Platforms list tab (``inspectInList``, 2026-06-12;
  // REQ-UI-051) so the synced highlight is visible without a manual tab switch.
  // Cross-pane links in this panel are *traversals*: they push the selection
  // being left onto the bus's navigation history so the back control can
  // return to it (REQ-UI-186). ``inspectInList`` does the same, plus the list
  // reveal, for links whose target has a list panel.
  const { inspectLinked } = useSelectionActions();
  const inspectInList = useInspectInList();
  // ``producer_id → display_name`` join for the DETECTED BY row label
  // (REQ-UI-110 parity with the Detections list rows). Falls back to the raw
  // id when the producers inventory hasn't resolved it; the click still selects
  // the platform either way (the id is what's wired, not the label).
  const names = useProducerNames();
  const sections = useSectionVisibility("detection");
  const { isVisible } = sections;
  const detection = query.data ?? null;
  const loading = query.isLoading;
  const error = query.isError ? query.error.message : null;

  // One shared "why is this section empty" note across the sections when
  // there's no detection payload to render (mirrors EventSections).
  const stateNote = loading
    ? "Loading detection…"
    : error !== null
      ? `Could not load detection: ${error}`
      : detection === null
        ? "Detection not found — it may have aged out of the queryable window."
        : null;

  return (
    <EntityDetailsSectionsProvider value={{ entityType: "detection", sections }}>
      {isVisible("identity") && (
        <SectionShell id="identity" title="Identity">
          <SectionRow
            label="Report ID"
            value={
              // Short, glanceable handle (DET-…) as the primary value; the full
              // ULID stays on hover/title + selectable so it's still
              // log-cross-referenceable (REQ-UI-066; CLAUDE.md §8.1).
              <code className="text-body-sm text-text-primary" title={reportId}>
                {shortId(reportId, "DET")}
              </code>
            }
          />
          {stateNote !== null && <SectionPlaceholder note={stateNote} />}
          {detection !== null && (
            <>
              <SectionRow
                label="Object ID"
                value={
                  <code className="text-body-sm text-text-primary" title={detection.object_id}>
                    {shortId(detection.object_id, "OBJ")}
                  </code>
                }
              />
              <SectionRow label="State" value={detection.state ?? "—"} />
              <SectionRow label="Detection time" value={formatTimestamp(detection.time)} />
            </>
          )}
        </SectionShell>
      )}

      {isVisible("classification") && (
        <ClassificationSection detection={detection} stateNote={stateNote} />
      )}
      {isVisible("track_object") && (
        <TrackObjectSection detection={detection} stateNote={stateNote} />
      )}
      {isVisible("geometry") && <GeometrySection detection={detection} stateNote={stateNote} />}
      {isVisible("signal") && <SignalSection detection={detection} stateNote={stateNote} />}
      {isVisible("behaviour") && <BehaviourSection detection={detection} stateNote={stateNote} />}
      {isVisible("associations") && (
        <AssociationsSection
          detection={detection}
          stateNote={stateNote}
          onSelectDetection={(objectId) => inspectLinked({ kind: "detection", id: objectId })}
        />
      )}

      {isVisible("detected_by") && (
        <SectionShell id="detected_by" title="Detected by">
          {detection === null && (
            <SectionPlaceholder note={stateNote ?? "No detecting platform."} />
          )}
          {detection !== null && (
            <SectionRow
              label="Platform"
              value={
                // Show the platform DISPLAY NAME (REQ-UI-110), not the raw
                // producer UUID; the click still selects the platform by id
                // (``inspect`` populates Entity Details + highlights the
                // Platforms list). ``title`` keeps the raw id reachable.
                <button
                  type="button"
                  className="font-mono text-body-sm text-meridian-action hover:underline"
                  onClick={() => inspectInList({ kind: "platform", id: detection.producer_id })}
                  data-testid="detection-detected-by-link"
                  title={detection.producer_id}
                >
                  {names.nameFor(detection.producer_id)}
                </button>
              }
            />
          )}
        </SectionShell>
      )}

      {isVisible("provenance") && <ProvenanceSection detection={detection} stateNote={stateNote} />}
      {/* The detection's own stored record IS the queryable
          ``sapient_detection`` row, keyed by this report id — so the drawer
          addresses it with what the selection bus already carries, and a
          detection reached from the Detections panel shows exactly what one
          reached from the Query panel shows (REQ-UI-183 / -184). */}
      {isVisible("source_bytes") && (
        <SourceBytesSection typeId={recordTypeForEntityKind("detection")} recordId={reportId} />
      )}
    </EntityDetailsSectionsProvider>
  );
}

// ---- Sections -----------------------------------------------------

interface SectionProps {
  detection: DetectionDetail | null;
  stateNote: string | null;
}

/**
 * CLASSIFICATION — the full ``classification[]`` tree. The first entry is
 * the primary class (REQ-UI-066); each carries a confidence and an
 * optional recursive ``sub_class`` refinement tree. An "Unclassified"
 * empty state when the detection carried no classification.
 */
function ClassificationSection({ detection, stateNote }: SectionProps): React.ReactElement {
  const classifications = detection?.classification ?? [];
  return (
    <SectionShell id="classification" title="Classification">
      {detection === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {detection !== null && classifications.length === 0 && (
        // No classification is a valid, common state (a bare track with
        // position but no inferred type), not an error — REQ-INGEST-044.
        <SectionPlaceholder note={classificationLabel(detection.primary_class)} />
      )}
      {classifications.map((cls, idx) => (
        <ClassificationRow key={`${cls.type}-${idx}`} cls={cls} primary={idx === 0} />
      ))}
    </SectionShell>
  );
}

function ClassificationRow({
  cls,
  primary,
}: {
  cls: DetectionClassification;
  primary: boolean;
}): React.ReactElement {
  return (
    <div data-testid="detection-classification-row">
      <SectionRow
        label={primary ? `${cls.type} (primary)` : cls.type}
        value={formatConfidence(cls.confidence)}
      />
      {(cls.sub_class ?? []).map((sub, idx) => (
        <SubClassRows key={`${sub.type}-${idx}`} sub={sub} depth={1} />
      ))}
    </div>
  );
}

/** Render a recursive ``sub_class`` refinement node + its descendants,
 * indenting one level per depth so the operator reads the tree shape. */
function SubClassRows({
  sub,
  depth,
}: {
  sub: DetectionSubClass;
  depth: number;
}): React.ReactElement {
  return (
    <div style={{ paddingLeft: `${depth * 12}px` }}>
      <SectionRow label={`↳ ${sub.type}`} value={formatConfidence(sub.confidence)} />
      {(sub.sub_class ?? []).map((child, idx) => (
        <SubClassRows key={`${child.type}-${idx}`} sub={child} depth={depth + 1} />
      ))}
    </div>
  );
}

/**
 * TRACK / OBJECT INFO — the ``track_info[]`` and ``object_info[]``
 * key/value lists (each a BSI ``DetectionKeyValue`` of type/value/error).
 * Both are optional; the section notes when neither is present.
 */
function TrackObjectSection({ detection, stateNote }: SectionProps): React.ReactElement {
  const trackInfo = detection?.track_info ?? [];
  const objectInfo = detection?.object_info ?? [];
  const empty = trackInfo.length === 0 && objectInfo.length === 0;
  return (
    <SectionShell id="track_object" title="Track / object info">
      {detection === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {detection !== null && <TrackMembershipRow detection={detection} />}
      {detection !== null && empty && (
        <SectionPlaceholder note="No track or object info reported." />
      )}
      {trackInfo.map((kv, idx) => (
        <KeyValueRow key={`track-${kv.type}-${idx}`} kv={kv} prefix="Track" />
      ))}
      {objectInfo.map((kv, idx) => (
        <KeyValueRow key={`object-${kv.type}-${idx}`} kv={kv} prefix="Object" />
      ))}
    </SectionShell>
  );
}

/**
 * The **Track** membership line (ADR-041 D-5; REQ-UI-175): ``×N · duration``
 * linking to the object track when the detection's track has two or more
 * members, "single detection" otherwise. The track is the BFF-derived
 * ``track_id`` on the detail (REQ-QUERY-053), resolved through the track
 * summary route — so it reads right for any detection, live or historical,
 * not only for one whose track is in the client buffer. The link navigates
 * Entity Details (``inspect``) and surfaces the Tracks list (``inspectInList``).
 */
function TrackMembershipRow({ detection }: { detection: DetectionDetail }): React.ReactElement {
  const inspectInList = useInspectInList();
  // Defensive against a pre-ADR-041 BFF that omits the field (the detail
  // shape guard does not require it): absent reads as "no track".
  const trackId = typeof detection.track_id === "string" ? detection.track_id : "";
  // Store first: a live track is already in the shared buffer (the Tracks
  // panel's poll, the map backfill, the SSE bridge), so the line reads
  // instantly and keeps counting as members arrive; the summary route is
  // the fallback for a detection whose track is not live (the first live
  // pass found that route queued for seconds behind the backfills).
  const live = useObjectTrackById(trackId !== "" ? trackId : null);
  const track = useObjectTrackDetail({ trackId: trackId !== "" && live === null ? trackId : null });
  const summary = track.data?.summary ?? null;
  if (trackId === "") {
    return <SectionRow label="Track" value="—" />;
  }
  const line =
    live !== null && isPresentedTrack(live)
      ? formatMembershipFromRecord(live)
      : summary !== null && summary.detection_count >= 2
        ? formatMembership(summary)
        : null;
  if (line === null && live === null && track.isLoading) {
    return <SectionRow label="Track" value="…" />;
  }
  if (line === null) {
    return (
      <SectionRow
        label="Track"
        value={<span data-testid="detection-track-membership">single detection</span>}
      />
    );
  }
  return (
    <SectionRow
      label="Track"
      value={
        <button
          type="button"
          className="font-mono text-body-sm text-meridian-action hover:underline"
          title={trackId}
          data-testid="detection-track-membership"
          onClick={() => inspectInList({ kind: "track", id: trackId })}
        >
          {line} →
        </button>
      }
    />
  );
}

function KeyValueRow({
  kv,
  prefix,
}: {
  kv: DetectionKeyValue;
  prefix: string;
}): React.ReactElement {
  // Surface the per-value error only when it's a meaningful (non-zero)
  // uncertainty — a 0 error is the "no reported uncertainty" default.
  const suffix = kv.error !== 0 ? ` (±${kv.error})` : "";
  return <SectionRow label={`${prefix} · ${kv.type}`} value={`${kv.value}${suffix}`} />;
}

/**
 * GEOMETRY — the projected WGS84 location, ENU velocity, and reported
 * colour. When the location is null (an unprojectable BSI range/bearing,
 * REQ-INGEST-044) the coordinate rows are replaced by an explanatory note —
 * and, when the **reporting platform's** position is known, the section says
 * the map glyph is anchored there (REQ-UI-071) and shows the platform's own
 * coordinates, mirroring the map's faint platform-anchored glyph.
 *
 * Pushing the detection to the map is the Detections-panel row right-click
 * → "Select on Map" (REQ-UI-096), the single push-to-map affordance — so
 * there is no per-section "Show on map" button here (removed 2026-06-09 as
 * redundant with the row menu).
 */
function GeometrySection({ detection, stateNote }: SectionProps): React.ReactElement {
  // Render the projected position in the operator's preferred coordinate
  // format (REQ-UI-084): one consolidated "Coordinates" row.
  const format = usePreferences().workingState.units?.coordinate_format ?? "decimal_degrees";
  // Bind the projected lat/lon as a non-null pair (or null when either axis
  // is absent) so the render below narrows cleanly without a cast.
  const position =
    detection !== null && detection.latitude_deg !== null && detection.longitude_deg !== null
      ? { lat: detection.latitude_deg, lon: detection.longitude_deg }
      : null;
  // Unprojectable range/bearing (REQ-INGEST-044): resolve the reporting
  // platform's latest status so we can state that the map glyph is anchored at
  // it (REQ-UI-071) and show the platform's coordinates. Gated to that case —
  // a located detection passes ``producerId: null`` so the query stays disabled
  // (no fetch). The producer-scoped endpoint dedupes against the panel's own
  // platform fetch (shared query key) when the same platform is in view.
  const needsAnchor = detection !== null && position === null;
  const anchorQuery = useProducerLatestStatus({
    producerId: needsAnchor && detection !== null ? detection.producer_id : null,
  });
  const anchorStatus = anchorQuery.data?.status ?? null;
  const platformAnchor =
    anchorStatus !== null &&
    typeof anchorStatus.latitude_deg === "number" &&
    typeof anchorStatus.longitude_deg === "number"
      ? { lat: anchorStatus.latitude_deg, lon: anchorStatus.longitude_deg }
      : null;
  return (
    <SectionShell id="geometry" title="Geometry">
      {detection === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {detection !== null && (
        <>
          {position !== null ? (
            <>
              <SectionRow
                label="Coordinates"
                value={formatCoordinate(position.lat, position.lon, format)}
              />
              <SectionRow label="Elevation" value={formatElevation(detection.elevation_m)} />
            </>
          ) : platformAnchor !== null ? (
            // Unprojectable, but the reporting platform has a known position —
            // the map anchors the glyph there (REQ-UI-071), so say so and show
            // the platform's coordinates. The detection's own typed columns
            // carry no lat/lng to show (operator-visibility, CLAUDE.md §8.1).
            <>
              <SectionRow
                label="Location"
                value="Approximate — shown at reporting platform (range/bearing not projected)"
              />
              <SectionRow
                label="Platform position"
                value={formatCoordinate(platformAnchor.lat, platformAnchor.lon, format)}
              />
            </>
          ) : (
            // Unprojectable AND no known reporting-platform position (nothing to
            // anchor to) — list-only on the map. The detail carries no raw geo
            // (the typed columns project only lat/lng/elev), so we note the
            // un-projectability rather than a phantom value.
            <SectionPlaceholder note="No projected location (range/bearing unprojectable)." />
          )}
          {detection.velocity != null && (
            <SectionRow label="Velocity (ENU)" value={formatVelocity(detection.velocity)} />
          )}
          {detection.colour !== "" && <SectionRow label="Colour" value={detection.colour} />}
        </>
      )}
    </SectionShell>
  );
}

/** SIGNAL — the ``signal[]`` RF characteristics (frequencies in Hz,
 * amplitude, pulse duration). Optional; notes when absent. */
function SignalSection({ detection, stateNote }: SectionProps): React.ReactElement {
  const signals = detection?.signal ?? [];
  return (
    <SectionShell id="signal" title="Signal">
      {detection === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {detection !== null && signals.length === 0 && (
        <SectionPlaceholder note="No RF signal characteristics reported." />
      )}
      {signals.map((sig, idx) => (
        <SignalRows key={idx} sig={sig} index={idx} multiple={signals.length > 1} />
      ))}
    </SectionShell>
  );
}

function SignalRows({
  sig,
  index,
  multiple,
}: {
  sig: DetectionSignal;
  index: number;
  multiple: boolean;
}): React.ReactElement {
  const prefix = multiple ? `#${index + 1} ` : "";
  return (
    <div data-testid="detection-signal-row">
      <SectionRow label={`${prefix}Centre freq`} value={formatHz(sig.centre_frequency)} />
      <SectionRow
        label={`${prefix}Band`}
        value={`${formatHz(sig.start_frequency)} – ${formatHz(sig.stop_frequency)}`}
      />
      <SectionRow label={`${prefix}Amplitude`} value={String(sig.amplitude)} />
      <SectionRow label={`${prefix}Pulse duration`} value={String(sig.pulse_duration)} />
    </div>
  );
}

/** BEHAVIOUR — the ``behaviour[]`` list (type + confidence). Optional. */
function BehaviourSection({ detection, stateNote }: SectionProps): React.ReactElement {
  const behaviours = detection?.behaviour ?? [];
  return (
    <SectionShell id="behaviour" title="Behaviour">
      {detection === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {detection !== null && behaviours.length === 0 && (
        <SectionPlaceholder note="No behaviours reported." />
      )}
      {behaviours.map((b, idx) => (
        <BehaviourRow key={`${b.type}-${idx}`} behaviour={b} />
      ))}
    </SectionShell>
  );
}

function BehaviourRow({ behaviour }: { behaviour: DetectionBehaviour }): React.ReactElement {
  return (
    <div data-testid="detection-behaviour-row">
      <SectionRow label={behaviour.type} value={formatConfidence(behaviour.confidence)} />
    </div>
  );
}

/**
 * ASSOCIATIONS — cross-sensor correlated detections + file attachments.
 * Each associated detection links to that detection via the bus (its
 * ``object_id`` is the cross-sensor correlation key); files show type +
 * url and are NOT auto-fetched (an air-gapped, possibly-large artifact).
 *
 * NOTE: the brief's ``derived_detection`` field is not on the PR4
 * :class:`DetectionDetail` gen type — only ``associated_detection[]`` /
 * ``associated_file[]`` are projected — so it is not rendered.
 */
function AssociationsSection({
  detection,
  stateNote,
  onSelectDetection,
}: SectionProps & {
  onSelectDetection: (objectId: string) => void;
}): React.ReactElement {
  const associated = detection?.associated_detection ?? [];
  const files = detection?.associated_file ?? [];
  const empty = associated.length === 0 && files.length === 0;
  return (
    <SectionShell id="associations" title="Associations">
      {detection === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {detection !== null && empty && (
        <SectionPlaceholder note="No associated detections or files." />
      )}
      {associated.map((assoc, idx) => (
        <AssociatedDetectionRow
          key={`${assoc.object_id}-${idx}`}
          assoc={assoc}
          onSelect={onSelectDetection}
        />
      ))}
      {files.map((file, idx) => (
        <AssociatedFileRow key={`${file.url}-${idx}`} file={file} />
      ))}
    </SectionShell>
  );
}

function AssociatedDetectionRow({
  assoc,
  onSelect,
}: {
  assoc: DetectionAssociatedDetection;
  onSelect: (objectId: string) => void;
}): React.ReactElement {
  return (
    <SectionRow
      label={`Node ${assoc.node_id}`}
      value={
        <button
          type="button"
          className="font-mono text-body-sm text-meridian-action hover:underline"
          onClick={() => onSelect(assoc.object_id)}
          data-testid="detection-associated-link"
        >
          {assoc.object_id}
        </button>
      }
    />
  );
}

function AssociatedFileRow({ file }: { file: DetectionAssociatedFile }): React.ReactElement {
  return (
    <SectionRow
      label={file.type || "File"}
      value={
        <span className="break-all font-mono text-body-sm text-text-secondary">{file.url}</span>
      }
    />
  );
}

/**
 * PROVENANCE — delivery + cross-network origin (REQ-UI-014). The CDS
 * source-network indicator follows the EventSections treatment: a text
 * indicator (the polished badge is ADR-016 Open Item 12).
 */
function ProvenanceSection({ detection, stateNote }: SectionProps): React.ReactElement {
  return (
    <SectionShell id="provenance" title="Provenance">
      {detection === null && <SectionPlaceholder note={stateNote ?? "—"} />}
      {detection !== null && (
        <>
          <SectionRow
            label="Delivered by"
            value={
              detection.source_aggregator_id ? (
                <code
                  className="text-body-sm text-text-primary"
                  title={detection.source_aggregator_id}
                >
                  {shortId(detection.source_aggregator_id, "AGG")}
                </code>
              ) : (
                "Direct producer (no aggregator)"
              )
            }
          />
          <SectionRow
            label="Network provenance"
            value={
              detection.source_network
                ? `Replicated via CDS from ${detection.source_network}`
                : "Locally ingested"
            }
          />
          <SectionRow label="Ingress time" value={formatTimestamp(detection.ingress_timestamp)} />
          <SectionRow label="Schema version" value={String(detection.schema_version)} />
          <SectionRow
            label="Message hash"
            value={
              <code className="text-body-sm text-text-secondary">
                {shortenHash(detection.message_hash)}
              </code>
            }
          />
        </>
      )}
    </SectionShell>
  );
}

// ---- Formatters ---------------------------------------------------

function formatTimestamp(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatElevation(elevationM: number | null): string {
  if (elevationM === null || !Number.isFinite(elevationM)) return "—";
  return `${elevationM.toFixed(1)} m`;
}

/** Format an ENU velocity as compact per-axis rates (m/s). */
function formatVelocity(velocity: components["schemas"]["DetectionVelocity"]): string {
  return `E ${velocity.east_rate} · N ${velocity.north_rate} · U ${velocity.up_rate} m/s`;
}

/** Format a frequency in Hz with a thousands separator for legibility. */
function formatHz(hz: number): string {
  if (!Number.isFinite(hz)) return "—";
  return `${hz.toLocaleString()} Hz`;
}

/** Shorten a 64-char hex hash to first-16 + ellipsis (mirrors the
 * platform CONFIGURATION cert-fingerprint treatment). */
function shortenHash(hash: string): string {
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 16)}…`;
}
